// import styles
import "./DocUpdate.css";

// import pacchetti
import { useState } from "react";
import format from "date-fns/format";
import { it } from "date-fns/locale";
import Select from "react-select";
import { filesCloud } from "../../firebase/config";

// import hooks
import { useFirestore } from "../../hooks/useFirestore";
import { useCollection } from "../../hooks/useCollection";

// Tipi di documento gestiti: file PDF completi con data di scadenza
export const documentTypes = [
  { value: "cartaIdentita", label: "Carta d'identità" },
  { value: "codiceFiscale", label: "Codice fiscale" },
  { value: "patente", label: "Patente" },
  { value: "permessoSoggiorno", label: "Permesso di soggiorno" },
  { value: "unilav", label: "Unilav" },
  { value: "visitaMedica", label: "Visita medica" },
  { value: "formazioneRischioAlto", label: "Formazione rischio alto" },
  { value: "formazionePreposto", label: "Formazione Preposto" },
  { value: "formazionePrimoSoccorso", label: "Formazione Primo Soccorso" },
  { value: "formazioneAntincendio", label: "Formazione Antincendio" },
  { value: "formazioneRls", label: "Formazione RLS" },
  { value: "formazioneRspp", label: "Formazione RSPP" },
  { value: "consegnaDpi", label: "Consegna DPI" },
];

// Documenti per cui è ammessa la scadenza "indeterminata"
const documentiScadenzaIndeterminata = ["unilav"];

export default function DocUpdate() {
  const { updateDocument } = useFirestore("users");
  const { documents: users } = useCollection("users");

  // state per gestire l'utente selezionato
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);

  // state per gestire il tipo di documento selezionato
  const [selectedDocType, setSelectedDocType] = useState(null);

  // state per gestire il file selezionato e i dati del documento
  const [selectedFile, setSelectedFile] = useState(null);
  const [scadenza, setScadenza] = useState("");
  const [indeterminato, setIndeterminato] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Preparazione delle opzioni per il dropdown degli utenti
  const userOptions = users
    ? users.map((user) => {
        let label = user.displayName || user.email;
        if (user.anagrafica) {
          label = `${user.anagrafica.nome} ${user.anagrafica.cognome}`;
        }
        return {
          value: user.id,
          label: label,
          userData: user,
        };
      })
    : [];

  // Indica se il tipo di documento selezionato ammette la scadenza indeterminata
  const scadenzaIndeterminabile =
    !!selectedDocType &&
    documentiScadenzaIndeterminata.includes(selectedDocType.value);

  // Gestione del cambio utente selezionato
  const handleUserChange = (option) => {
    setSelectedUser(option);
    setSelectedUserData(option.userData);
    setSelectedDocType(null);
    setSelectedFile(null);
    setScadenza("");
    setIndeterminato(false);
    setFileError(null);
    setCurrentDocument(null);
  };

  // Gestione del cambio tipo documento
  const handleDocTypeChange = (option) => {
    setSelectedDocType(option);
    setSelectedFile(null);
    setFileError(null);

    // Recupera il documento già caricato dello stesso tipo, se presente
    const documenti = selectedUserData?.documenti || [];
    const doc = documenti.find((d) => d.tipo === option.value) || null;
    setCurrentDocument(doc);
    setScadenza(doc?.scadenza || "");
    setIndeterminato(doc?.indeterminato === true);
  };

  // Gestione del file selezionato (solo PDF)
  const handleFileChange = (e) => {
    setSelectedFile(null);
    setFileError(null);

    const selected = e.target.files[0];

    if (!selected) {
      setFileError("Inserisci un file");
      return;
    }

    const isPdf =
      selected.type === "application/pdf" ||
      selected.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setFileError("Il file selezionato dev'essere un PDF");
      return;
    }

    setSelectedFile(selected);
  };

  // Gestione dell'invio del form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // La scadenza non è richiesta se il documento ammette l'indeterminato ed è spuntato
    const scadenzaObbligatoria = !(scadenzaIndeterminabile && indeterminato);

    if (
      !selectedUser ||
      !selectedDocType ||
      !selectedFile ||
      (scadenzaObbligatoria && !scadenza)
    ) {
      setFileError(
        "Seleziona un utente, un tipo di documento, un file PDF e la data di scadenza",
      );
      return;
    }

    setIsUploading(true);
    setFileError(null);

    try {
      const email = selectedUserData.email || selectedUser.value;

      // Crea il percorso del file nella cartella documenti dell'anagrafica
      const filePath = `anagrafiche/${email}/documenti/${selectedDocType.value}_${Date.now()}.pdf`;

      // Carica il file
      const fileRef = await filesCloud.ref(filePath).put(selectedFile);

      // Ottieni l'URL del file caricato
      const fileUrl = await fileRef.ref.getDownloadURL();

      // Costruisci il nuovo documento
      const newDocument = {
        tipo: selectedDocType.value,
        label: selectedDocType.label,
        url: fileUrl,
        scadenza: scadenzaObbligatoria ? scadenza : "",
        indeterminato: scadenzaIndeterminabile && indeterminato,
        nomeFile: selectedFile.name,
        dataCaricamento: new Date().toISOString(),
      };

      // Sostituisci l'eventuale documento dello stesso tipo e mantieni gli altri
      const documenti = selectedUserData.documenti || [];
      const updatedDocumenti = [
        ...documenti.filter((d) => d.tipo !== selectedDocType.value),
        newDocument,
      ];

      // Aggiorna il documento dell'utente
      await updateDocument(selectedUser.value, { documenti: updatedDocumenti });

      // Aggiorna lo stato locale per riflettere la modifica immediatamente
      const updatedUserData = {
        ...selectedUserData,
        documenti: updatedDocumenti,
      };
      setSelectedUserData(updatedUserData);
      setSelectedUser({ ...selectedUser, userData: updatedUserData });
      setCurrentDocument(newDocument);
      setSelectedFile(null);

      alert("Documento caricato con successo!");
    } catch (error) {
      console.error("Errore durante il caricamento del documento:", error);
      setFileError("Errore durante il caricamento del documento");
    }

    setIsUploading(false);
  };

  return (
    <div className="anagrafica-form">
      <h2 className="page-title">Aggiornamento Documenti</h2>

      <form onSubmit={handleSubmit}>
        <label>
          <span>Seleziona Utente:</span>
          <Select
            options={userOptions}
            onChange={handleUserChange}
            value={selectedUser}
            placeholder="Seleziona un utente..."
            isSearchable
          />
        </label>

        {selectedUser && (
          <label>
            <span>Seleziona Documento:</span>
            <Select
              options={documentTypes}
              onChange={handleDocTypeChange}
              value={selectedDocType}
              placeholder="Seleziona un documento..."
            />
          </label>
        )}

        {selectedDocType && currentDocument && (
          <div className="document-preview">
            <h3>Documento Attuale</h3>
            <p>
              <a
                href={currentDocument.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {currentDocument.nomeFile || currentDocument.label}
              </a>
            </p>
            {currentDocument.indeterminato ? (
              <p>Scadenza: Indeterminato</p>
            ) : currentDocument.scadenza ? (
              <p>
                Scadenza:{" "}
                {format(new Date(currentDocument.scadenza), "d MMM yyyy", {
                  locale: it,
                })}
              </p>
            ) : null}
          </div>
        )}

        {selectedDocType && (
          <>
            <label>
              <span>Carica Nuovo Documento (PDF):</span>
              <input
                type="file"
                id="pdfFile"
                accept="application/pdf,.pdf"
                onChange={handleFileChange}
              />
              {fileError && <div className="error">{fileError}</div>}
            </label>

            {scadenzaIndeterminabile && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={indeterminato}
                  onChange={(e) => setIndeterminato(e.target.checked)}
                />
                <span>Indeterminato</span>
              </label>
            )}

            {!(scadenzaIndeterminabile && indeterminato) && (
              <label>
                <span>Data di scadenza:</span>
                <input
                  type="date"
                  value={scadenza}
                  onChange={(e) => setScadenza(e.target.value)}
                />
              </label>
            )}

            <button
              className="btn"
              disabled={
                !selectedFile ||
                (!(scadenzaIndeterminabile && indeterminato) && !scadenza) ||
                isUploading
              }
            >
              {isUploading ? "Caricamento..." : "Aggiorna Documento"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
